import { useCallback, useEffect, useRef, useState } from "react";

import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { ContactPoint } from "@/Common/Api/Contacts/Dto/ContactPoint";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { EmailContact } from "@/Common/Api/Contacts/Dto/EmailContact";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { AddressControlDTO } from "@/Common/Components/Contacts/AddressControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { AddressesEdit } from "@/Patient/Components/Profile/ContactComponents/AddressesEdit";
import { EmailsEdit } from "@/Patient/Components/Profile/ContactComponents/EmailsEdit";
import { PhonesEdit } from "@/Patient/Components/Profile/ContactComponents/PhonesEdit";
import { ContactsEditModelHelper } from "../Helpers/ContactsEditModelHelper";
import { Relative } from "../Models/Relative";
import { ContactsEditModel } from "../Models/UI/ContactsEditModel";
import { relativeStore } from "../Stores/RelativeStore";

function ContactsEdit(): JSX.Element {
    const relative = relativeStore.GetCurrent();
    const file = relativeStore.GetCurrentFile();
    const { getText } = useTranslation();
    const contactsRef: React.MutableRefObject<ContactsEditModel> = useRef(); // State is not available on component unmount

    let contactPointClones = MyleHelper.CloneObject<ContactPoint[]>(relative.ContactPoints || []);
    let phoneClones = MyleHelper.CloneObject<PhoneNumber[]>(relative.Phones || []);
    let addressesClones = MyleHelper.CloneObject<Address[]>(relative.Addresses || []);
    let emailClones = MyleHelper.CloneObject<Email[]>(relative.Emails || []);

    // reset validation
    addressesClones.forEach(
        address => address.IsValid = !!address.ZipCode
            ? ValidationHelper.validatePostalCode(address.ZipCode, address.CountryLookupId)
            : true
    );

    const [contacts, setContacts] = useState<ContactsEditModel>(
        file.ActiveTabSavedState
            ? file.ActiveTabSavedState // Restore saved state
            : {
                contactPoints: contactPointClones,
                addresses: addressesClones,
                phones: phoneClones,
                emails: emailClones,
                doNotEmail: relative.DoNotEmail
            }
    );

    const isSaveActionEnabled = useCallback(() => {
        if (contacts) {
            return contacts.addresses.all(address => address.IsValid || Address.IsEmpty(address))
                && contacts.phones.all(phone => phone.IsValid || PhoneNumber.IsEmpty(phone))
                && contacts.emails.all(email => email.IsValid || Email.IsEmpty(email));
        }

        return true;
    }, [contacts]);

    useEffect(() => {
        $(window).trigger("resize");
        contactsRef.current = contacts;
    }, [contacts, isSaveActionEnabled]);

    useEffect(
        () =>
            () => {
                // Save state on unmount
                sessionStore.dispatch(LocalOperations.Relative.SaveProfileState, { state: contactsRef.current, tab: ProfileTab.Contacts });
            }
        , [] // Empty array mean component unmount. We want the state saved once, not after every key stroke.
    );

    if (addressesClones.length === 0) {
        const contactPoint = ContactsEditModelHelper.createNewEmptyContactPoint(relative.ContactId);
        const address = ContactsEditModelHelper.createNewEmptyAddress(contactPoint);
        contactPointClones.push(contactPoint);
        addressesClones.push(address);
    }

    if (phoneClones.length === 0) {
        const contactPoint = ContactsEditModelHelper.createNewEmptyContactPoint(relative.ContactId);
        const phone = ContactsEditModelHelper.createNewEmptyPhone(contactPoint);
        contactPointClones.push(contactPoint);
        phoneClones.push(phone);
    }

    if (emailClones.length === 0) {
        const contactPoint = ContactsEditModelHelper.createNewEmptyContactPoint(relative.ContactId);
        const email = ContactsEditModelHelper.createNewEmptyEmail(contactPoint);
        contactPointClones.push(contactPoint);
        emailClones.push(email);
    }

    // Only show warning message if there is at least one filled email.
    const showWarningMessage = contacts.emails?.some(x => x.Address.trim().length);

    const onAddAddress = (): void => {
        const contactPoint = ContactsEditModelHelper.createNewEmptyContactPoint(relative.ContactId);
        const address = ContactsEditModelHelper.createNewEmptyAddress(contactPoint);
        const newContact = { ...contacts };
        newContact.addresses = [...newContact.addresses];
        newContact.addresses.push(address);
        newContact.contactPoints = [...newContact.contactPoints];
        newContact.contactPoints.push(contactPoint);
        setContacts(newContact);
    };

    const onRemoveAddress = (item: Address): void => {
        const newContact = { ...contacts };
        newContact.addresses = newContact.addresses.filter(a => a.ContactPoint_id !== item.ContactPoint_id);
        newContact.contactPoints = newContact.contactPoints.filter(c => c.Id !== item.ContactPoint_id);
        setContacts(newContact);

        if (newContact.addresses.length === 0) {
            const contactPoint = ContactsEditModelHelper.createNewEmptyContactPoint(relative.ContactId);
            const address = ContactsEditModelHelper.createNewEmptyAddress(contactPoint);
            newContact.addresses.push(address);
            newContact.contactPoints.push(contactPoint);
            setContacts(newContact);
        }
    };

    const onNotifyParentAddressCallback = (address: Address, dto: AddressControlDTO, index: number): void => {
        if (dto.selectedCountryLookupId !== address.CountryLookupId) {
            const newContact = { ...contacts };
            newContact.addresses = [...newContact.addresses];
            newContact.addresses[index].CountryLookupId = dto.selectedCountryLookupId;
            setContacts(newContact);
        }

        if (dto.selectedRegionLookupId !== address.StateLookupId) {
            const newContact = { ...contacts };
            newContact.addresses = [...newContact.addresses];
            newContact.addresses[index].StateLookupId = dto.selectedRegionLookupId;
            setContacts(newContact);
        }

        if (dto.civicAddressEdit !== address.StreetAddress) {
            const newContact = { ...contacts };
            newContact.addresses = [...newContact.addresses];
            newContact.addresses[index].StreetAddress = dto.civicAddressEdit;
            newContact.addresses[index].IsValid = addressIsValid(dto);
            setContacts(newContact);
        }

        if (dto.cityEdit !== address.City) {
            const newContact = { ...contacts };
            newContact.addresses = [...newContact.addresses];
            newContact.addresses[index].City = dto.cityEdit;
            newContact.addresses[index].IsValid = addressIsValid(dto);
            setContacts(newContact);
        }

        if (dto.postalCodeEdit !== address.ZipCode) {
            const newContact = { ...contacts };
            newContact.addresses = [...newContact.addresses];
            newContact.addresses[index].ZipCode = dto.postalCodeEdit;
            newContact.addresses[index].IsValid = addressIsValid(dto);
            setContacts(newContact);
        }
    };

    const addressIsValid = (address: AddressControlDTO): boolean =>
        (!address.postalCodeEdit || ValidationHelper.validatePostalCode(address.postalCodeEdit, address.selectedCountryLookupId));

    const onAddressLookupChange = (value: string, index: number): void => {
        const newContact = { ...contacts };
        newContact.addresses = [...newContact.addresses];
        newContact.addresses[index].ContactPointTypeLookupId = value;
        newContact.contactPoints.firstOrDefault((contactPoint: ContactPoint) =>
            contactPoint.Id == newContact.addresses[index].ContactPoint_id
        ).ContactPointTypeLookupId = value;
        setContacts(newContact);
    };

    const onAddressPreferenceChange = (isChecked: boolean, index: number): void => {
        const newContact = { ...contacts };
        newContact.addresses = [...newContact.addresses];
        newContact.addresses.forEach((address, idx) => {
            const contactPoint = contacts.contactPoints.firstOrDefault(cp => cp.Id == address.ContactPoint_id);
            if (idx === index) {
                address.IsPreferred = isChecked;
                contactPoint.IsPreferred = isChecked;
            }
            else {
                address.IsPreferred = false;
                contactPoint.IsPreferred = false;
            }
        });
        setContacts(newContact);
    };

    const onAddPhone = (): void => {
        const contactPoint = ContactsEditModelHelper.createNewEmptyContactPoint(relative.ContactId);
        const phone = ContactsEditModelHelper.createNewEmptyPhone(contactPoint);
        const newContact = { ...contacts };
        newContact.phones = [...newContact.phones];
        newContact.phones.push(phone);
        newContact.contactPoints = [...newContact.contactPoints];
        newContact.contactPoints.push(contactPoint);
        setContacts(newContact);
    };

    const onRemovePhone = (item: PhoneNumber): void => {
        const newContact = { ...contacts };
        newContact.phones = newContact.phones.filter(a => a.ContactPoint_id !== item.ContactPoint_id);
        newContact.contactPoints = newContact.contactPoints.filter(c => c.Id !== item.ContactPoint_id);
        setContacts(newContact);

        if (newContact.phones.length === 0) {
            const contactPoint = ContactsEditModelHelper.createNewEmptyContactPoint(relative.ContactId);
            const phone = ContactsEditModelHelper.createNewEmptyPhone(contactPoint);
            newContact.phones.push(phone);
            newContact.contactPoints.push(contactPoint);
            setContacts(newContact);
        }
    };

    const onPhoneTypeSelect = (value: string, index: number): void => {
        const contactPointLookupId = value.split("_")[0];
        const phoneLookupId = value.split("_")[1];

        const newContact = { ...contacts };
        newContact.phones = [...newContact.phones];
        newContact.phones[index].ContactPointTypeLookupId = contactPointLookupId;
        newContact.phones[index].PhoneTypeLookupId = phoneLookupId;
        newContact.contactPoints.firstOrDefault((contactPoint: ContactPoint) =>
            contactPoint.Id == newContact.phones[index].ContactPoint_id
        ).ContactPointTypeLookupId = contactPointLookupId;

        if (newContact.phones[index].PhoneTypeLookupId !== cacheStore.getLookupByPhoneType(PhoneType.Mobile)?.LookupID) {
            newContact.phones[index].NotificationsEnabled = false;
        }

        setContacts(newContact);
    };

    const onPhoneCountryTypeSelect = (value: string, index: number): void => {
        const newContact = { ...contacts };
        newContact.phones = [...newContact.phones];
        newContact.phones[index].CountryLookupId = value;
        setContacts(newContact);
    };

    const onPhoneNumberChange = (value: string, index: number): void => {
        const newContact = { ...contacts };
        newContact.phones = [...newContact.phones];
        newContact.phones[index].Number = value;
        ValidationHelper.validatePhoneNumber(newContact.phones[index]);
        setContacts(newContact);
    };

    const onPhoneExtChange = (value: string, index: number): void => {
        const newContact = { ...contacts };
        newContact.phones = [...newContact.phones];
        newContact.phones[index].Extension = value;
        setContacts(newContact);
    };

    const onPhoneNoteChange = (value: string, index: number): void => {
        const newContact = { ...contacts };
        newContact.phones = [...newContact.phones];
        newContact.phones[index].Notes = value;
        setContacts(newContact);
    };

    const onPhoneIsPreferredChange = (isChecked: boolean, index: number): void => {
        const newContact = { ...contacts };
        newContact.phones = [...newContact.phones];
        newContact.phones.forEach((phone, idx) => {
            const contactPoint = contacts.contactPoints.firstOrDefault(cp => cp.Id == phone.ContactPoint_id);
            if (idx === index) {
                phone.IsPreferred = isChecked;
                contactPoint.IsPreferred = isChecked;
            }
            else {
                phone.IsPreferred = false;
                contactPoint.IsPreferred = false;
            }
        });
        setContacts(newContact);
    };

    const onPhoneDefaultNotificationChange = (isChecked: boolean, index: number): void => {
        const newContact = { ...contacts };
        newContact.phones = [...newContact.phones];
        newContact.phones[index].NotificationsEnabled = isChecked;
        setContacts(newContact);
    };

    const onAddEmail = (): void => {
        const contactPoint = ContactsEditModelHelper.createNewEmptyContactPoint(relative.ContactId);
        const email = ContactsEditModelHelper.createNewEmptyEmail(contactPoint);
        const newContact = { ...contacts };
        newContact.emails = [...newContact.emails];
        newContact.emails.push(email);
        newContact.contactPoints = [...newContact.contactPoints];
        newContact.contactPoints.push(contactPoint);
        setContacts(newContact);
    };

    const onRemoveEmail = (item: Email): void => {
        const newContact = { ...contacts };
        newContact.emails = newContact.emails.filter(email => email.ContactPoint_id !== item.ContactPoint_id);
        newContact.contactPoints = newContact.contactPoints.filter(c => c.Id !== item.ContactPoint_id);
        setContacts(newContact);

        if (newContact.emails.length === 0) {
            const contactPoint = ContactsEditModelHelper.createNewEmptyContactPoint(relative.ContactId);
            const email = ContactsEditModelHelper.createNewEmptyEmail(contactPoint);
            newContact.emails.push(email);
            newContact.contactPoints.push(contactPoint);
            setContacts(newContact);
        }
    };

    const onDoNotEmailChange = (isChecked: boolean): void => {
        const newContact = { ...contacts };
        newContact.doNotEmail = isChecked;
        setContacts(newContact);
    };

    const onEmailContactTypeChange = (value: string, index: number): void => {
        const newContact = { ...contacts };
        newContact.emails = [...newContact.emails];
        newContact.emails[index].ContactPointTypeLookupId = value;
        newContact.contactPoints.firstOrDefault((contactPoint: ContactPoint) =>
            contactPoint.Id == newContact.emails[index].ContactPoint_id
        ).ContactPointTypeLookupId = value;
        setContacts(newContact);
    };

    const onEmailChange = (value: string, index: number): void => {
        const newContact = { ...contacts };
        newContact.emails = [...newContact.emails];
        newContact.emails[index].Address = value;
        newContact.emails[index].IsValid = EmailContact.validateEmail(value, false);
        setContacts(newContact);
    };

    const onEmailDefaultNotificationChange = (isChecked: boolean, index: number): void => {
        const newContact = { ...contacts };
        newContact.emails = [...newContact.emails];
        newContact.emails[index].NotificationsEnabled = isChecked;
        newContact.contactPoints.firstOrDefault((contactPoint: ContactPoint) =>
            contactPoint.Id == newContact.emails[index].ContactPoint_id
        ).NotificationsEnabled = isChecked;
        setContacts(newContact);
    };

    const onEmailOkToContactChange = (isChecked: boolean, index: number): void => {
        const newContact = { ...contacts };
        newContact.emails = [...newContact.emails];
        newContact.emails[index].IsOkToContact = isChecked;
        setContacts(newContact);
    };

    const onSave = (): void => {
        const updatedRelative = MyleHelper.CloneObject<Relative>(relative);
        updatedRelative.ContactPoints = contacts.contactPoints;
        updatedRelative.Addresses = contacts.addresses.filter(address => address.IsValid && !Address.IsEmpty(address));
        updatedRelative.Phones = contacts.phones.filter(phone => phone.IsValid && !PhoneNumber.IsEmpty(phone));
        updatedRelative.Emails = contacts.emails.filter(email => email.IsValid && !Email.IsEmpty(email));
        updatedRelative.DoNotEmail = contacts.doNotEmail;
        relativeStore.actions.updateContacts(updatedRelative);
    };

    return (
        <div className="tabComponent contacts" data-cy="patientProfile-contacts-edit">
            <div className="tabComponent-main patient-prof">
                <div className="block separated">
                    <div className="flex-container heading">
                        <h3 className="thin-font">{getText("ContactInformationLabel")}</h3>
                    </div>
                    <div className="patient-profile fxd-wdth-container edit">
                        <div className="tb">
                            <div className="tbc left">
                                <AddressesEdit
                                    addresses={contacts.addresses}
                                    onAdd={() => onAddAddress()}
                                    onRemove={address => onRemoveAddress(address)}
                                    onNotifyParentCallback={(address, dto, index) => onNotifyParentAddressCallback(address, dto, index)}
                                    onContactTypeChange={(value, index) => onAddressLookupChange(value, index)}
                                    onPreferenceChange={(isChecked, index) => onAddressPreferenceChange(isChecked, index)}
                                />
                            </div>
                            <div className="tbc right">
                                <PhonesEdit
                                    phones={contacts.phones}
                                    onAdd={() => onAddPhone()}
                                    onRemove={phone => onRemovePhone(phone)}
                                    onNumberChange={(value, index) => onPhoneNumberChange(value, index)}
                                    onTypeSelect={(value, index) => onPhoneTypeSelect(value, index)}
                                    onCountrySelect={(value, index) => onPhoneCountryTypeSelect(value, index)}
                                    onExtChange={(value, index) => onPhoneExtChange(value, index)}
                                    onNoteChange={(value, index) => onPhoneNoteChange(value, index)}
                                    onDefaultNotificationChange={(isChecked, index) => onPhoneDefaultNotificationChange(isChecked, index)}
                                    onIsPreferredChange={(isChecked, index) => onPhoneIsPreferredChange(isChecked, index)}
                                />
                                <EmailsEdit
                                    emails={contacts.emails}
                                    doNotEmail={contacts.doNotEmail}
                                    showWarningMessage={showWarningMessage}
                                    onAdd={() => onAddEmail()}
                                    onEmailChange={(value, index) => onEmailChange(value, index)}
                                    onRemove={email => onRemoveEmail(email)}
                                    onDoNotEmailChange={isChecked => onDoNotEmailChange(isChecked)}
                                    onContactTypeChange={(value, index) => onEmailContactTypeChange(value, index)}
                                    onDefaultNotificationChange={(isChecked, index) => onEmailDefaultNotificationChange(isChecked, index)}
                                    onOkToContactChange={(isChecked, index) => onEmailOkToContactChange(isChecked, index)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="tabComponent-footer ws-subfooter patient-profile">
                <div className="tb patient-profile-footer">
                    <div className="tbc pull-right">
                        <ButtonControl
                            className="btn btn-default push-right-5"
                            onClick={() => sessionStore.dispatch(LocalOperations.Relative.CancelProfileEdition)}
                            label={getText("CancelLabel")}
                        />
                        <ButtonControl
                            className="btn btn-primary"
                            label={getText("SaveLabel")}
                            onClick={() => onSave()}
                            disabled={!isSaveActionEnabled()}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export { ContactsEdit };
